import { EventBus, Events } from "jiffy-store";
import { api } from "jiffy-api";
import { track } from "jiffy-analytics";
import { redirectTo } from "common/utils/browser";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { trackProductAddedModal } from "components/jiffy/product-details-quantity-form/analytics";
import { on } from "common/utils/ui/event";
import ModalFileUpload from "./modal-file-upload";
import "./added-to-cart-modal-v2.scss";

const $$getModal = () => $("#added-to-cart-modal-v2");
const $getPromoTitleMobile = () =>
  document.querySelector(".js-added-to-cart-modal-v2-promo-title-mobile");
const $getPromoTitleDesktop = () =>
  document.querySelector(".js-added-to-cart-modal-v2-promo-title-desktop");

let initialized = false;
let cachedDtfDeliveryDate = null;
let uploadHandler = null;
let trackingAdditionalProps = {};

const DTF_PRODUCT_SLUG = "jiffytransfers-DTF001";
const DTF_COMPOSITION_NAME = "Transfer";

/**
 * Fetch delivery date for DTF transfers
 * @returns {Promise<string|null>} The formatted delivery date or null
 */
const fetchDtfDeliveryDate = async () => {
  if (cachedDtfDeliveryDate) {
    return cachedDtfDeliveryDate;
  }

  try {
    const payload = {
      composition_name: DTF_COMPOSITION_NAME,
      variants_hash: JSON.stringify({}),
      pre_cut: false,
      dtf_print: [{ height: 1, width: 1, quantity: 1 }]
    };

    const response = await api.product.fetchDeliveryEstimatesForCurrentCart(
      { productId: DTF_PRODUCT_SLUG },
      payload
    );

    if (response && response.composition_dates && response.composition_dates.value_full) {
      cachedDtfDeliveryDate = response.composition_dates.value_full;
      return cachedDtfDeliveryDate;
    }

    return null;
  } catch (error) {
    console.warn("Failed to fetch DTF delivery date:", error);
    return null;
  }
};

/**
 * Update the delivery date in the modal
 * @param {string} deliveryDate The formatted delivery date
 */
const updateModalDeliveryDate = deliveryDate => {
  const $deliveryDateSpan = document.querySelector(
    "#added-to-cart-modal-v2 .added-to-cart-modal-v2__delivery-date"
  );
  if ($deliveryDateSpan && deliveryDate) {
    $deliveryDateSpan.textContent = deliveryDate;
  }
};

/**
 * Update the modal promo title
 * @param {HTMLElement} $element The element to update
 * @param {string} title The promo title text to update
 */
const updateModalPromoTitle = ($element, title) => {
  if ($element && title) {
    $element.textContent = title;
    $element.classList.remove("hidden");
  }
};

/**
 * Handle chat link click to open Tidio chat widget
 */
const handleChatClick = event => {
  event.preventDefault();
  event.stopPropagation();

  // Disable Bootstrap modal focus enforcement to allow typing in Tidio chat
  $(document).off("focusin.bs.modal");

  const chatWidget = document.getElementById("tidio-chat");
  if (chatWidget) {
    setTimeout(() => {
      window.tidioChatApi?.show?.();
      window.tidioChatApi?.open?.();
    }, 100);
  }
};

// eslint-disable-next-line max-lines-per-function
const initModal = () => {
  const $modal = document.getElementById("added-to-cart-modal-v2");
  if ($modal) {
    uploadHandler = new ModalFileUpload($modal);
  }

  on("click", "#added-to-cart-modal-v2 .js-button-view-cart", () => {
    trackProductAddedModal("view_cart", {
      ...trackingAdditionalProps,
      modal: "free_transfers_atc",
      items_added: undefined,
      items_value: undefined
    });
    $$getModal().modal("hide");
    redirectTo("/cart");
  });

  on("click", "#added-to-cart-modal-v2 .js-button-continue-shopping", () => {
    submitDtfEvent("dtf_continue_shopping_modal_click", {
      action: "continue_shopping",
      business_line: trackingAdditionalProps.business_line || "Transfers",
      free_shipping_threshold: trackingAdditionalProps.free_shipping_threshold,
      catalog_number: trackingAdditionalProps.catalog_number,
      modal: "free_transfers_atc"
    });
  });

  on("click", "#added-to-cart-modal-v2 .js-modal-upload-label", () => {
    submitDtfEvent("transfer_choose_file_clicked", {
      page: "pdp",
      pdp_type: "blanks",
      catalog_number: trackingAdditionalProps.catalog_number || "DTF001",
      modal: "free_transfers_atc"
    });
  });

  on("change", "#added-to-cart-modal-v2 .js-modal-file-input", e => {
    const [file] = e.target.files;
    if (!file) return;

    e.target.value = "";

    if (uploadHandler && uploadHandler.handleFileSelection(file)) {
      uploadHandler.uploadArtwork();
    }
  });

  on("click", "#added-to-cart-modal-v2 .js-modal-retry-button", () => {
    const $fileInput = document.getElementById("modal-file-input");
    if ($fileInput) {
      $fileInput.click();
    }
  });

  on("click", "#added-to-cart-modal-v2 .js-chat-link", handleChatClick);

  EventBus.on(Events.ITEMS_ADDED_TO_CART, ({ target }) => {
    const { free_shipping_threshold: shippingThreshold } = target || {};
    const $continueShoppingModal = document.getElementById("continue-shopping-modal");
    const businessLine = $continueShoppingModal?.dataset?.businessLine || "Transfers";
    const catalogNumber = $continueShoppingModal?.dataset?.catalogNumber;

    trackingAdditionalProps = {
      free_shipping_threshold: shippingThreshold,
      business_line: businessLine,
      catalog_number: catalogNumber
    };
  });

  EventBus.on(Events.SHOW_ADDED_TO_CART_MODAL_V2, async (data = {}) => {
    const $$modal = $$getModal();
    if ($$modal.length > 0) {
      // Hide the regular modal if it's showing
      const $$regularModal = $("#continue-shopping-modal");
      if ($$regularModal.length > 0 && $$regularModal.is(":visible")) {
        $$regularModal.modal("hide");
      }

      if (data.trackingProps) {
        trackingAdditionalProps = { ...data.trackingProps };
      }

      if (!trackingAdditionalProps.business_line) {
        trackingAdditionalProps.business_line = "Transfers";
      }

      $$modal.modal("show");

      track("added_to_cart_modal_opened", { modal_type: "Free Transfers" });

      uploadHandler?.resetUI?.();
      // Use BOGO message from backend
      const bogoMessage =
        data.bogoMessage || data.target?.bogoMessage || "Buy blanks, get free transfers!";

      updateModalPromoTitle($getPromoTitleMobile(), bogoMessage);
      updateModalPromoTitle($getPromoTitleDesktop(), bogoMessage);

      const deliveryDate = await fetchDtfDeliveryDate();
      if (deliveryDate) {
        updateModalDeliveryDate(deliveryDate);
      }
    }
  });
};

export default () => {
  if (!initialized) {
    initModal();
    initialized = true;
  }
};
